import { ArrowDown, ArrowUp, Plus, Trash } from '@phosphor-icons/react';
import { useListMediaCollections } from '@reelplicate/api-client/media-collections';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import type { SlideSlot } from '@reelplicate/flows';
import {
  Button,
  Field,
  IconButton,
  Inline,
  Select,
  SelectItem,
  Stack,
  TextInput,
} from '@reelplicate/ui';
import { useId, useState } from 'react';

import {
  fallbackOption,
  useCaptionStyleOptions,
} from '../../../caption-styles/caption-style-options';
import { useSlotImages } from '../../../caption-styles/slot-images';
import { type EditorStart, StyleEditor } from '../../../caption-styles/style-editor';
import { StylePicker } from '../../../caption-styles/style-picker';
import type { FieldSpec } from './config-fields';

const NONE = '__none__';
const POSITIONS: SlideSlot['position'][] = ['top', 'center', 'bottom'];
const POSITION_LABELS = { top: 'Top', center: 'Middle', bottom: 'Bottom' };

type Slot = Partial<SlideSlot>;

/**
 * Carousel from slots' slides, in order: each takes its images from one
 * hand-picked collection and its text from a collection of captions (or has
 * none), drawn at the top, middle or bottom.
 */
export function SlotsField({
  workspaceId,
  field,
  value,
  carouselStyle,
  error,
  onChange,
}: {
  workspaceId: string;
  field: FieldSpec;
  value: unknown;
  /** The carousel's caption style, which slides use unless they pick their own. */
  carouselStyle?: string;
  error?: string;
  onChange: (value: unknown) => void;
}) {
  const labelId = useId();
  const slots = Array.isArray(value) ? (value as Slot[]) : [];
  const { data } = useListMediaCollections(workspaceId, {
    query: { enabled: Boolean(workspaceId), throwOnError: false },
  });
  // Hand-picked or smart: a smart pool is whatever matches when the run starts.
  const collections = data ?? [];

  const { data: workspaces } = useListWorkspaces();
  const workspaceDefault = workspaces?.find(
    (w) => w.id === workspaceId,
  )?.defaultCaptionStyle;
  const styles = useCaptionStyleOptions(workspaceId);
  const carousel =
    styles.find(carouselStyle) ?? fallbackOption(styles.find, workspaceDefault);
  const images = useSlotImages(
    workspaceId,
    slots.map((s) => s.imagesCollectionId),
  );
  // The slide a new style is being made for.
  const [editor, setEditor] = useState<{ slide: number; start: EditorStart } | null>(
    null,
  );

  const set = (next: Slot[]) => onChange(next);
  const update = (i: number, patch: Slot) =>
    set(slots.map((slot, j) => (j === i ? { ...slot, ...patch } : slot)));
  const move = (from: number, to: number) => {
    const next = [...slots];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    set(next);
  };

  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-col gap-2">
      <span id={labelId} className="text-label-md text-text-primary">
        {field.label}
      </span>
      {field.hint && <p className="text-caption text-text-secondary">{field.hint}</p>}
      {data && collections.length === 0 && (
        <p className="text-body-sm text-text-secondary">
          Make a collection per slide in Media (e.g. “Hook slides”, “Hook captions”) to
          pick from here.
        </p>
      )}
      <Stack as="ol" gap={3}>
        {slots.map((slot, i) => (
          <Stack
            as="li"
            key={i}
            gap={2}
            aria-label={`Slide ${i + 1}`}
            className="border-border-subtle rounded-lg border p-3"
          >
            <Inline gap={1}>
              <span className="text-label-sm text-text-secondary w-5">{i + 1}.</span>
              <TextInput
                size="sm"
                aria-label={`Slide ${i + 1} name`}
                placeholder="e.g. Hook"
                value={slot.label ?? ''}
                maxLength={40}
                onChange={(event) => update(i, { label: event.target.value })}
                className="min-w-0 flex-1"
              />
              <IconButton
                icon={ArrowUp}
                size="sm"
                aria-label={`Move slide ${i + 1} earlier`}
                disabled={i === 0}
                onClick={() => move(i, i - 1)}
              />
              <IconButton
                icon={ArrowDown}
                size="sm"
                aria-label={`Move slide ${i + 1} later`}
                disabled={i === slots.length - 1}
                onClick={() => move(i, i + 1)}
              />
              <IconButton
                icon={Trash}
                size="sm"
                aria-label={`Remove slide ${i + 1}`}
                onClick={() => set(slots.filter((_, j) => j !== i))}
              />
            </Inline>
            <Field label="Images from">
              <Select
                size="sm"
                placeholder="Pick a collection"
                value={slot.imagesCollectionId}
                onValueChange={(id) => update(i, { imagesCollectionId: id })}
              >
                {collections.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </Select>
            </Field>
            <Field label="Text from">
              <Select
                size="sm"
                value={slot.captionsCollectionId ?? NONE}
                onValueChange={(id) =>
                  update(i, { captionsCollectionId: id === NONE ? undefined : id })
                }
              >
                <SelectItem value={NONE}>No text</SelectItem>
                {collections.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </Select>
            </Field>
            {slot.captionsCollectionId && (
              <Field label="Text sits at">
                <Select
                  size="sm"
                  value={slot.position ?? 'bottom'}
                  onValueChange={(position) =>
                    update(i, { position: position as SlideSlot['position'] })
                  }
                >
                  {POSITIONS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {POSITION_LABELS[p]}
                    </SelectItem>
                  ))}
                </Select>
              </Field>
            )}
            {slot.captionsCollectionId && (
              <Field label="Caption style">
                <StylePicker
                  title={`Caption style · Slide ${i + 1}${slot.label ? `, ${slot.label}` : ''}`}
                  options={styles.all}
                  value={slot.style}
                  imageUrl={images[0]}
                  followsCarousel={!slot.style}
                  onFollowCarousel={(follow) =>
                    update(i, { style: follow ? undefined : carousel.ref })
                  }
                  onSelect={(ref) => update(i, { style: ref })}
                  onNewStyle={() =>
                    setEditor({
                      slide: i,
                      start: {
                        mode: 'create',
                        style: (styles.find(slot.style) ?? carousel).style,
                      },
                    })
                  }
                  trigger={
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      aria-label={`Slide ${i + 1} caption style`}
                      className="justify-start"
                    >
                      {slot.style
                        ? (styles.find(slot.style)?.name ?? 'Deleted style')
                        : `Carousel style · ${carousel.name}`}
                    </Button>
                  }
                />
              </Field>
            )}
          </Stack>
        ))}
      </Stack>
      {slots.length < 10 && (
        <Button
          size="sm"
          variant="outline"
          leadingIcon={Plus}
          className="self-start"
          onClick={() =>
            set([...slots, { label: `Slide ${slots.length + 1}`, position: 'bottom' }])
          }
        >
          Add slide
        </Button>
      )}
      {error && <p className="text-caption text-status-danger-fg">{error}</p>}
      <StyleEditor
        workspaceId={workspaceId}
        start={editor?.start ?? null}
        images={images}
        onOpenChange={(open) => !open && setEditor(null)}
        onSaved={(saved) => editor && update(editor.slide, { style: saved.id })}
      />
    </div>
  );
}
